import { useState, useEffect, useLayoutEffect, useRef } from "react";
import api from "../../api/client";
import { PageSection } from "../PageSection";
import { PageHeader } from "../PageHeader";
import { OfferingGrid } from "../OfferingGrid";
import { groupItemsByCategory } from "../../utils/menu";

export const Menu = () => {
  const [menus, setMenus] = useState([]);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuData, setMenuData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglesStacked, setTogglesStacked] = useState(false);
  const toggleRowRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [menusRes, itemsRes] = await Promise.all([
          api.get("/menu/menus"),
          api.get("/menu"),
        ]);

        const menusList = (menusRes.data || []).filter((m) => m.id !== 0);
        setMenus(menusList);
        if (menusList.length > 0 && activeMenuId === null) {
          setActiveMenuId(menusList[0].id);
        }

        const byMenu = {};
        for (const item of itemsRes.data || []) {
          (byMenu[item.menuId] ||= []).push(item);
        }
        const grouped = {};
        for (const [mId, items] of Object.entries(byMenu)) {
          grouped[mId] = groupItemsByCategory(items);
        }
        setMenuData(grouped);
      } catch (err) {
        setError("Failed to load menu data");
        console.error("Error fetching menu:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect overflow and stack toggle buttons vertically when needed
  useLayoutEffect(() => {
    const el = toggleRowRef.current;
    if (!el || !el.parentElement) return;

    const measure = () => {
      const wasStacked = el.classList.contains("is-stacked");
      if (wasStacked) el.classList.remove("is-stacked");
      const overflows = el.scrollWidth > el.clientWidth + 1;
      if (wasStacked) el.classList.add("is-stacked");
      setTogglesStacked(overflows);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el.parentElement);
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => ro.disconnect();
  }, [menus]);

  const currentCategories = activeMenuId != null ? menuData[activeMenuId] || {} : {};

  return (
    <PageSection className="menu" id="menu">
      <PageHeader
        className="menu-header"
        tagline="Our Menu"
        subtitle="Please explore our bougie selection!"
      />

      {menus.length > 1 && (
        <div
          ref={toggleRowRef}
          className={`menu-toggle-buttons${togglesStacked ? " is-stacked" : ""}`}
        >
          {menus.map((m) => (
            <button
              key={m.id}
              className={`menu-toggle-btn ${activeMenuId === m.id ? "active" : ""}`}
              onClick={() => setActiveMenuId(m.id)}
            >
              <span className="btn-icon"></span>
              {m.name}
            </button>
          ))}
        </div>
      )}

      <OfferingGrid
        loading={loading}
        error={error}
        categories={currentCategories}
        gridClassName="menu-items"
        categoryClassName="menu-category"
        loadingText="Loading menu..."
        emptyText="No menu items available"
        renderItem={(item) => (
          <div key={item.id} className="menu-item">
            {item.image && (
              <img
                src={`/image_uploads/${item.image}`}
                alt={item.name}
                className="menu-item-image"
                loading="lazy"
              />
            )}
            <h4>{item.name}</h4>
            <span className="price">{item.price}</span>
            {item.description?.trim() && (
              <p className="menu-description">{item.description}</p>
            )}
          </div>
        )}
      />
    </PageSection>
  );
};
